import type { SourcingProjectView } from "@shared/labs/sourcing";
import { ArrowLeft, Radar, Sparkles, Target } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { rpcClient } from "@/lib/rpc";
import { ExploreFlow } from "./explore-flow";
import { PreciseFlow } from "./precise-flow";
import { ProjectsTab } from "./projects/projects-tab";
import type { FlowProject } from "./projects/run-project-actions";

type Mode = "projects" | "explore" | "precise";

/**
 * Deep links into this page:
 *  - `?exploration=<id>&kind=<flow>` — a shared run; drops the recipient into
 *    that run's results (the read is owner-shared and free).
 *  - `?project=<id>&kind=<flow>` — "new session from a project": the flow opens
 *    with the project's brief prefilled and the run links back to the project.
 *  - anything else (incl. the bare page and `?tab=projects` back-navs) lands on
 *    Projetos — the home view.
 */
function readDeepLink(): { explorationId?: string; projectId?: string; mode: Mode } {
  const p = new URLSearchParams(window.location.search);
  const explorationId = p.get("exploration") ?? undefined;
  const projectId = p.get("project") ?? undefined;
  const flow: Mode = p.get("kind") === "precise" ? "precise" : "explore";
  return { explorationId, projectId, mode: explorationId || projectId ? flow : "projects" };
}

/**
 * Sourcing (Sondar). PROJETOS — one open-position tracker per vaga — is the
 * home view; the two search flows over the shared pipeline are reachable from
 * the header's top-right buttons:
 *  - Explorar: the autonomous contextual-bandit loop (probe → judge → harvest).
 *  - Busca precisa: `understand` proposes filters, the recruiter edits EVERY
 *    filter, then runs one exact faceted search.
 * Both flows return the same FIT-ranked, explained shortlist you can push to an
 * LA set — or into a project.
 */
export default function LabsSourcing() {
  const deepLink = useMemo(readDeepLink, []);
  const [mode, setMode] = useState<Mode>(deepLink.mode);
  // Hand the deep-linked id to the flow that matches its kind, so it reopens on mount.
  const sharedId = (m: Mode) => (m === deepLink.mode ? deepLink.explorationId : undefined);

  // "New session from a project": load the project once so the flow starts with
  // its brief + LA scope and links the run back. Free (one projectGet).
  const [project, setProject] = useState<FlowProject | undefined>(undefined);
  const [projectLoading, setProjectLoading] = useState(Boolean(deepLink.projectId));
  useEffect(() => {
    if (!deepLink.projectId) return;
    let cancelled = false;
    rpcClient.labs
      .sourcingProjectGet({ projectId: deepLink.projectId })
      .then((v: SourcingProjectView) => {
        if (cancelled) return;
        setProject({
          id: v.id,
          name: v.name,
          brief: v.brief,
          laModelId: v.laModelId ?? null,
          laDisciplineId: v.laDisciplineId ?? null,
          laLevel: v.laLevel ?? null,
        });
        setProjectLoading(false);
      })
      .catch(() => {
        if (!cancelled) setProjectLoading(false); // fall back to a standalone run
      });
    return () => {
      cancelled = true;
    };
  }, [deepLink.projectId]);

  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p
            className="mb-1 font-semibold uppercase"
            style={{
              fontSize: "11px",
              letterSpacing: 0,
              lineHeight: 1.4,
              color: "var(--ink-muted)",
            }}
          >
            Alpha · Labs
          </p>
          <PageHeader
            title="Sourcing"
            description="Sondar — sourcing de candidatos no LinkedIn para Brasil, LATAM e US. Um projeto por vaga; explore e faça buscas precisas a partir dele."
            icon={<Radar className="h-5 w-5 text-muted-foreground" />}
          />
        </div>

        {/* The search flows live behind the top-right buttons; Projetos is home. */}
        <div className="mt-1 flex shrink-0 items-center gap-2">
          {mode !== "projects" && (
            <Button variant="ghost" size="sm" onClick={() => setMode("projects")}>
              <ArrowLeft /> Projetos
            </Button>
          )}
          <Button
            variant={mode === "explore" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("explore")}
          >
            <Sparkles /> Explorar
          </Button>
          <Button
            variant={mode === "precise" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("precise")}
          >
            <Target /> Busca precisa
          </Button>
        </div>
      </div>

      <div className="mt-6">
        {mode === "projects" ? (
          <ProjectsTab />
        ) : projectLoading ? null : mode === "explore" ? (
          <ExploreFlow initialExplorationId={sharedId("explore")} project={project} />
        ) : (
          <PreciseFlow initialExplorationId={sharedId("precise")} project={project} />
        )}
      </div>
    </>
  );
}
